<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import { useAuthenticatedMediaUrl } from '@/modules/chat-matrix-fast/composables/useAuthenticatedMediaUrl'
import { rememberWarmDirectAvatar } from '@/modules/chat-matrix-fast/utils/avatarWarmPaint'
import { isSameAvatarAsset } from '@/modules/chat-matrix-fast/utils/userDataExtractor'

const props = withDefaults(defineProps<{
  src?: string | null
  alt?: string
  purpose?: 'avatar' | 'attachment' | 'poster'
}>(), {
  src: null,
  alt: '',
  purpose: 'avatar',
})

const emit = defineEmits<{
  (e: 'error'): void
}>()

const { resolvedSrc, hasError } = useAuthenticatedMediaUrl(
  toRef(() => props.src || undefined),
  { purpose: props.purpose },
)

/** Last painted frame — only while parent clears src (same chat switch race). */
const stickySrc = ref('')

watch(
  resolvedSrc,
  value => {
    if (!value || hasError.value) {
      return
    }
    stickySrc.value = value
    rememberWarmDirectAvatar(value)
  },
)

const displaySrc = computed(() => {
  if (resolvedSrc.value && !hasError.value) {
    return resolvedSrc.value
  }
  // Keep last frame only when src is briefly cleared — never across a different asset.
  if (
    !props.src
    && stickySrc.value
  ) {
    return stickySrc.value
  }
  if (
    props.src
    && stickySrc.value
    && isSameAvatarAsset(stickySrc.value, props.src)
  ) {
    return stickySrc.value
  }
  return resolvedSrc.value || ''
})

watch(hasError, value => {
  if (value) {
    emit('error')
  }
})

function handleError() {
  emit('error')
}

function handleLoad(event: Event) {
  const img = event.target as HTMLImageElement | null
  const painted = img?.currentSrc || img?.src
  if (painted) {
    rememberWarmDirectAvatar(painted)
  }
}
</script>

<template>
  <!-- Native img: VImg fade/transition caused empty flashes on src change. -->
  <img
    v-if="displaySrc"
    class="authenticated-v-img"
    :src="displaySrc"
    :alt="alt"
    draggable="false"
    @load="handleLoad"
    @error="handleError"
  >
</template>

<style scoped>
.authenticated-v-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
